// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import AssetDropdown from "./AssetDropDown";
import EthereumAsset from "../../api/types/ethereumAsset";
import StellarAsset from "../../api/types/stellarAsset";
import { NervosAsset } from "../../api/types/nervosAsset";
import { LPCellInfo } from "../../api/types/liquidity";
import { Hubs } from "../../constants/Constants";
import {
  CKB_DECIMALS,
  lpFormatShares,
  lpFromHuman,
  lpMaxWithdrawShannons,
  lpToHuman,
  lpTruncateAddress,
} from "../../constants/lpUnits";
import { mapLPError } from "./lpErrors";
import CopyButton from "./CopyButton";

type AnyAsset = EthereumAsset | StellarAsset | NervosAsset;

// Withdraws CKB (by cell + amount) or ETH (by shares to burn) liquidity.
function LPWithdrawForm() {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);

  const [assets, setAssets] = useState<Map<string, AnyAsset>>(new Map());
  const [selected, setSelected] = useState<string>();
  const [cells, setCells] = useState<LPCellInfo[]>([]);
  const [lpCellID, setLpCellID] = useState<string>();
  const [destination, setDestination] = useState("");
  const [ethShares, setEthShares] = useState("");
  const [amount, setAmount] = useState("");
  const [sharesToBurn, setSharesToBurn] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const map = new Map<string, AnyAsset>();
    ctx.client
      .getAssets({ type: "all" })
      .then((resp) => {
        // The ETH↔CKB pool only takes native ETH and native CKB — exclude
        // tokens like PRN (ERC20) and SUDTs.
        if (ctx.client.ethAddress && resp.ethereumAssets) {
          resp.ethereumAssets
            .filter((a) => a.type === "ETH")
            .forEach((a) => map.set(a.assetID(), a));
        }
        if (ctx.client.nervosAddress && resp.nervosAssets) {
          resp.nervosAssets
            .filter((a) => a.type === "CKB")
            .forEach((a) => map.set(a.assetID(), a));
        }
        setAssets(map);
      })
      .catch((err) => setError(mapLPError(err)));
  }, [ctx.client]);

  const selectedAsset = selected ? assets.get(selected) : undefined;
  const isNervos = selectedAsset instanceof NervosAsset;

  // Load the LP's positions when an asset is selected: the CKB cells to pick
  // from (plus the wallet address the withdrawal pays out to), or the ETH
  // share balance for the Max button.
  useEffect(() => {
    if (!selectedAsset) {
      return;
    }
    (async () => {
      try {
        if (isNervos) {
          const ownerLockHash =
            (await ctx.client.ckbProvider?.getOwnerLockHash()) ?? "";
          const resp = await ctx.client.getLPPositions(ownerLockHash, "");
          setCells(resp.ckbCells);
          if (resp.ckbCells.length > 0) {
            setLpCellID(resp.ckbCells[0].outpoint);
          }
          // Withdrawn CKB always goes to the connected wallet's own address.
          const dest =
            (await ctx.client.ckbProvider?.getRecommendedAddress()) ?? "";
          setDestination(dest);
        } else {
          const ethOwner = ctx.client.ethAddress?.toString() ?? "";
          const resp = await ctx.client.getLPPositions("", ethOwner);
          setEthShares(resp.ethShares);
        }
      } catch (err) {
        setError(mapLPError(err));
      }
    })();
  }, [ctx.client, isNervos, selectedAsset]);

  const selectedCell = cells.find((c) => c.outpoint === lpCellID);
  // What the selected cell can pay out right now (available minus the 343 CKB
  // state rent and the flat tx fee).
  const maxWithdrawShannons = selectedCell
    ? lpMaxWithdrawShannons(selectedCell.availableCKB)
    : "0";

  const submit = async () => {
    setMessage(undefined);
    setError(undefined);
    if (!selectedAsset) {
      return;
    }
    const hub = Hubs.get(selectedAsset.chainID)?.address ?? "";
    const channelAsset = selectedAsset.toChannelAsset();
    setBusy(true);
    try {
      if (selectedAsset instanceof NervosAsset) {
        if (!lpCellID) {
          setError(t("lpInvalidAmount"));
          return;
        }
        // The backend wants the owner's full CKB (bech32m) address, not the
        // omnilock internal/eth address.
        const ownerAddress =
          destination ||
          (await ctx.client.ckbProvider!.getRecommendedAddress());
        const resp = await ctx.client.lpWithdrawCKB(channelAsset, hub, {
          lpCellID,
          ckbOut: lpFromHuman(amount, CKB_DECIMALS),
          ownerAddress,
        });
        setMessage(t("lpWithdrawSuccess", { tx: resp.txHash }));
      } else {
        if (BigInt(sharesToBurn || "0") <= BigInt(0)) {
          setError(t("lpInvalidAmount"));
          return;
        }
        await ctx.client.lpWithdrawETH(channelAsset, sharesToBurn, hub);
        setMessage(t("lpWithdrawSuccess", { tx: "confirmed" }));
      }
    } catch (err) {
      setError(mapLPError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form
      className="w-100"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Form.Group className="mb-3">
        <Form.Label>{t("lpAsset")}</Form.Label>
        <AssetDropdown
          assets={assets}
          selectedAsset={selected}
          onSelectAsset={(id) => id && setSelected(id)}
        />
      </Form.Group>

      {isNervos ? (
        <>
          <Form.Group className="mb-3">
            <Form.Label>{t("lpSelectCell")}</Form.Label>
            <Form.Select
              value={lpCellID}
              onChange={(e) => setLpCellID(e.currentTarget.value)}
            >
              {cells.map((c) => (
                <option key={c.outpoint} value={c.outpoint}>
                  {lpToHuman(c.availableCKB, CKB_DECIMALS)} CKB —{" "}
                  {lpTruncateAddress(c.outpoint)}
                </option>
              ))}
            </Form.Select>
            {selectedCell && (
              <Form.Text className="text-muted" title={t("lpWithdrawableNowNote")}>
                {t("lpWithdrawableNow")}:{" "}
                {lpToHuman(maxWithdrawShannons, CKB_DECIMALS)} CKB
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("lpAmount")}</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                inputMode="decimal"
                value={amount}
                placeholder="0.0"
                onChange={(e) => setAmount(e.currentTarget.value)}
              />
              <Button
                variant="outline-secondary"
                disabled={!selectedCell}
                onClick={() =>
                  setAmount(lpToHuman(maxWithdrawShannons, CKB_DECIMALS))
                }
              >
                {t("lpMax")}
              </Button>
            </InputGroup>
          </Form.Group>
          {destination && (
            <Form.Group className="mb-3">
              <Form.Label>{t("lpDestination")}</Form.Label>
              <div title={destination}>
                <code>{lpTruncateAddress(destination)}</code>
                <CopyButton value={destination} />
              </div>
              <Form.Text className="text-muted">
                {t("lpDestinationNote")}
              </Form.Text>
            </Form.Group>
          )}
        </>
      ) : (
        <Form.Group className="mb-3">
          <Form.Label>{t("lpSharesToBurn")}</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              inputMode="numeric"
              value={sharesToBurn}
              placeholder="0"
              onChange={(e) => setSharesToBurn(e.currentTarget.value)}
            />
            <Button
              variant="outline-secondary"
              disabled={!ethShares}
              onClick={() => setSharesToBurn(ethShares)}
            >
              {t("lpMax")}
            </Button>
          </InputGroup>
          <Form.Text className="text-muted">
            {ethShares
              ? `${t("lpYourShares")}: ${lpFormatShares(ethShares)} — `
              : ""}
            {t("lpSharesToBurnNote")}
          </Form.Text>
        </Form.Group>
      )}

      {message && <p style={{ color: "#00C9B8" }}>{message}</p>}
      {error && (
        <span className="invalid-feedback" style={{ display: "block" }}>
          {error}
        </span>
      )}

      <Button
        type="submit"
        disabled={
          busy ||
          !selected ||
          (isNervos ? !lpCellID || !amount : !sharesToBurn)
        }
      >
        {busy ? t("lpLoading") : t("lpWithdrawSubmit")}
      </Button>
    </Form>
  );
}

export default LPWithdrawForm;
